/**
 * @typedef {import('./types').throttle} throttle
 * @typedef {import('./types').debounce} debounce
 * @typedef {import('./types').updateCartStateDisplays} updateCartStateDisplays
 * @typedef {import('./types').AnimatedDetails} AnimatedDetails
 */

/** @type {throttle} */
function throttle(fn, timeout) {
  let ready = true;

  return (...args) => {
    if (!ready) return;

    ready = false;

    fn(...args);

    setTimeout(() => {
      ready = true;
    }, timeout);
  };
}

/** @type {debounce} */
function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout>} */
  let timeout;

  return (...args) => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      fn(...args);
    }, wait);
  };
}

/** @type {updateCartStateDisplays} */
async function updateCartStateDisplays(count = undefined, total = undefined) {
  const counters = Array.from(document.querySelectorAll('[data-cart-item-count]'));
  const totals = Array.from(document.querySelectorAll('[data-cart-total]'));

  /** @param {string} count - The number of items in the cart */
  function setCartItemCount(count) {
    counters.forEach((c) => {
      c.setAttribute('data-cart-item-count', count);
      c.textContent = count;
    });
  }

  /** @param {string} total - The total price of the cart */
  function setCartTotal(total) {
    totals.forEach((t) => {
      t.setAttribute('data-cart-total', total);
      t.textContent = total;
    });
  }

  /** @param {string} count - The number of items in the cart */
  function setCheckoutButtons(count) {
    const checkouts = document.querySelectorAll('[name="checkout"]');

    const int = Number(count);

    checkouts.forEach((c) => {
      if (int > 0) {
        c.removeAttribute('disabled');
      } else {
        c.setAttribute('disabled', '');
      }
    });
  }

  if (counters.length > 0 && count && totals.length > 0 && total) {
    setCartTotal(total);
    setCartItemCount(count);
    setCheckoutButtons(count);

    return;
  }

  const request = await fetch(`${window.Shopify.routes.root}?sections=section-main-cart`);

  if (!request.ok) {
    throw new Error(request.statusText);
  }

  const data = await request.json();

  if (data['section-main-cart']) {
    const html = new DOMParser().parseFromString(data['section-main-cart'], 'text/html');
    const component = html.querySelector('cart-form');

    if (component && component instanceof HTMLElement) {
      const total = component.dataset.cartTotalValue;
      const quantity = component.dataset.cartQuantityValue;

      if (total && quantity) {
        setCartTotal(total);
        setCartItemCount(quantity);
        setCheckoutButtons(quantity);
      }
    }
  }
}

window.traps = {
  create: (target, options) => {
    if (target) {
      const id = target.dataset.focusTrap;

      if (id) {
        const trap = window.focusTrap.createFocusTrap(target, options);

        if (trap) {
          window.traps.store[id] = trap;

          return window.traps.store[id];
        }
      }
    }
  },
  deactivate: (excluded) => {
    Object.values(window.traps.store).forEach((trap) => {
      if (excluded) {
        if (!excluded.includes(trap)) {
          trap.deactivate();
        }
      } else {
        trap.deactivate();
      }
    });
  },
  store: {},
};

addEventListener('click', (e) => {
  const target = e.target;

  if (target && target instanceof Element) {
    const data = target.closest('[data-focus-trap-toggle]');

    if (data && data instanceof HTMLElement) {
      const id = data.dataset.focusTrapToggle;

      if (id && window.traps.store[id]) {
        const trap = window.traps.store[id];

        if (!trap.active) {
          if (!target.parentElement?.closest('[data-focus-trap]')) {
            window.traps.deactivate();
          }

          trap.activate();
        } else {
          trap.deactivate();
        }
      }
    }
  }
});

if (!customElements.get('modal-trap')) {
  customElements.define(
    'modal-trap',
    class ModalTrap extends HTMLElement {
      constructor() {
        super();
        this.trap = undefined;

        /** @type {HTMLElement | null} */
        this.modal = this.querySelector('[data-focus-trap]');

        /** @type {HTMLButtonElement | null} */
        this.fallback = this.querySelector('[data-focus-trap-fallback]');

        /** @type {string[]} */
        this.tabs = [];
      }

      connectedCallback() {
        if (this.modal) {
          const id = this.modal.dataset.focusTrap;

          this.tabs = Array.from(this.modal.querySelectorAll('[data-tab-id]'))
            .map((button) => {
              if (button instanceof HTMLElement && button.dataset.tabId) {
                return button.dataset.tabId;
              }

              return '';
            })
            .filter(Boolean);

          if (id && !window.traps.store[id]) {
            this.trap = window.traps.create(this.modal, {
              onActivate: () => {
                const event = new CustomEvent('modal-trap-activate', {
                  detail: {
                    id,
                    trap: this.trap,
                  },
                });

                this.modal?.setAttribute('aria-expanded', 'true');

                if (this.getAttribute('data-frozen-body-scroll') !== 'false') {
                  document.body.setAttribute('data-frozen-scroll', 'true');
                }

                window.dispatchEvent(event);
              },
              onDeactivate: () => {
                const event = new CustomEvent('modal-trap-deactivate', {
                  detail: {
                    id,
                    trap: this.trap,
                  },
                });

                this.modal?.setAttribute('aria-expanded', 'false');

                window.dispatchEvent(event);
              },

              onPostDeactivate: () => {
                if (!Object.values(window.traps.store).some((trap) => trap.active)) {
                  document.body.removeAttribute('data-frozen-scroll');
                }
              },
              fallbackFocus: this.fallback ?? undefined,
              clickOutsideDeactivates: this.getAttribute('data-click-outside-deactivates') !== 'false',
              escapeDeactivates: this.getAttribute('data-escape-deactivates') !== 'false',
            });
          }
        }
      }
    },
  );
}

if (!customElements.get('cookie-consent')) {
  customElements.define(
    'cookie-consent',
    class CookieConsent extends HTMLElement {
      constructor() {
        super();

        /** @type {string} */
        this.handle = this.querySelector('[data-focus-trap]')?.getAttribute('data-focus-trap') || 'cookies';

        this.trap = window.traps.store[this.handle];

        /** @type {HTMLInputElement | null} */
        this.settingAnalytics = this.querySelector('[data-cookies-accept-analytics]');

        /** @type {HTMLInputElement | null} */
        this.settingMarketing = this.querySelector('[data-cookies-accept-marketing]');

        /** @type {HTMLInputElement | null} */
        this.settingPreferences = this.querySelector('[data-cookies-accept-preferences]');

        /** @type {HTMLInputElement | null} */
        this.acceptAll = this.querySelector('[data-cookies-accept-all]');

        /** @type {HTMLInputElement | null} */
        this.rejectAll = this.querySelector('[data-cookies-reject-all]');

        /** @type {HTMLButtonElement | null} */
        this.save = this.querySelector('[data-cookies-save]');

        /** @type {HTMLDivElement | null} */
        this.actions = this.querySelector('[data-cookies-actions]');

        /** @type {HTMLButtonElement | null} */
        this.expand = this.querySelector('[data-cookies-expand]');

        /** @type {HTMLButtonElement | null} */
        this.collapse = this.querySelector('[data-cookies-collapse]');

        /** @type {AnimatedDetails | null} */
        this.details = this.querySelector('animated-details');
      }

      connectedCallback() {
        window.consent = {
          marketing: '',
          analytics: '',
          preferences: '',
          sale_of_data: '',
        };

        window.Shopify.loadFeatures(
          [
            {
              name: 'consent-tracking-api',
              version: '0.1',
            },
          ],
          async (/** @type {any} */ error) => {
            if (error) {
              throw error;
            }

            const consent = await window.Shopify.customerPrivacy.currentVisitorConsent();

            window.consent = {
              marketing: consent.marketing,
              analytics: consent.analytics,
              preferences: consent.preferences,
              sale_of_data: consent.sale_of_data,
            };

            window.gtag('consent', 'update', {
              ad_storage: window.consent.marketing === 'yes' ? 'granted' : 'denied',
              analytics_storage: window.consent.analytics === 'yes' ? 'granted' : 'denied',
              ad_user_data: window.consent.marketing === 'yes' ? 'granted' : 'denied',
              ad_personalization: window.consent.marketing === 'yes' ? 'granted' : 'denied',
            });

            if (this.shouldShowBanner() && this.trap) this.trap.activate();

            if (this.save) this.save.addEventListener('click', () => this.setConsent());

            if (this.acceptAll) this.acceptAll.addEventListener('click', () => this.setAcceptAll());

            if (this.rejectAll) this.rejectAll.addEventListener('click', () => this.setRejectAll());

            if (this.settingMarketing && this.settingAnalytics && this.settingPreferences) {
              this.settingMarketing.checked = !!window.consent.marketing;
              this.settingAnalytics.checked = !!window.consent.analytics;
              this.settingPreferences.checked = !!window.consent.preferences;
            }
          },
        );

        if (this.actions && this.expand && this.collapse && this.details) {
          this.expand.addEventListener('click', () => {
            this.actions?.setAttribute('data-expanded', 'true');
            this.details?.toggle();

            const fallbackFocusElement = this.querySelector('[data-cookies-option]');

            if (fallbackFocusElement && fallbackFocusElement instanceof HTMLElement) {
              fallbackFocusElement.focus();
            }
          });

          this.collapse.addEventListener('click', () => {
            this.actions?.removeAttribute('data-expanded');
            this.details?.toggle();
            this.expand?.removeAttribute('data-hidden');
            this.expand?.focus();
          });

          this.expand.addEventListener('transitionend', () => {
            if (this.actions?.hasAttribute('data-expanded')) {
              this.expand?.setAttribute('data-hidden', 'true');
            }
          });
        }

        document.addEventListener('visitorConsentCollected', () => {
          /** @type {import('./types').LocalizationBanner | null} */
          const localization = document.querySelector('localization-banner');

          if (
            localization &&
            !!localization.suggestion &&
            localStorage.getItem('viewed-localization-banner') !== 'true'
          ) {
            localization.trap?.activate();
          }
        });
      }

      setConsent() {
        if (this.settingMarketing && this.settingAnalytics && this.settingPreferences) {
          const settings = {
            marketing: this.settingMarketing.checked,
            analytics: this.settingAnalytics.checked,
            preferences: this.settingPreferences.checked,
            sale_of_data: window.Shopify.customerPrivacy.saleOfDataAllowed(),
          };

          window.Shopify.customerPrivacy.setTrackingConsent(settings, () => {
            window.consent = {
              marketing: settings.marketing ? 'yes' : 'no',
              analytics: settings.analytics ? 'yes' : 'no',
              preferences: settings.preferences ? 'yes' : 'no',
              sale_of_data: settings.sale_of_data ? 'yes' : 'no',
            };

            if (this.trap) this.trap.deactivate();
          });
        }
      }

      setAcceptAll() {
        if (this.settingMarketing && this.settingAnalytics && this.settingPreferences) {
          const settings = {
            marketing: true,
            analytics: true,
            preferences: true,
            sale_of_data: window.Shopify.customerPrivacy.saleOfDataAllowed(),
          };

          window.Shopify.customerPrivacy.setTrackingConsent(settings, () => {
            window.consent = {
              marketing: settings.marketing ? 'yes' : 'no',
              analytics: settings.analytics ? 'yes' : 'no',
              preferences: settings.preferences ? 'yes' : 'no',
              sale_of_data: settings.sale_of_data ? 'yes' : 'no',
            };

            if (this.trap) this.trap.deactivate();
          });
        }
      }

      setRejectAll() {
        if (this.settingMarketing && this.settingAnalytics && this.settingPreferences) {
          const settings = {
            marketing: false,
            analytics: false,
            preferences: false,
            sale_of_data: window.Shopify.customerPrivacy.saleOfDataAllowed(),
          };

          window.Shopify.customerPrivacy.setTrackingConsent(settings, () => {
            window.consent = {
              marketing: settings.marketing ? 'yes' : 'no',
              analytics: settings.analytics ? 'yes' : 'no',
              preferences: settings.preferences ? 'yes' : 'no',
              sale_of_data: settings.sale_of_data ? 'yes' : 'no',
            };

            if (this.trap) this.trap.deactivate();
          });
        }
      }

      shouldShowBanner() {
        if (location.hostname === '127.0.0.1' || !!window.Shopify.designMode) return false;

        return Object.values(window.consent).some((value) => value === '');
      }
    },
  );
}

if (!customElements.get('cart-form')) {
  customElements.define(
    'cart-form',
    class CartForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.controller = null;
      }

      connectedCallback() {
        if (this.form) {
          this.form.addEventListener('change', this.changeHandler.bind(this));
          this.form.addEventListener('click', this.deleteHandler.bind(this));
        }
      }

      /**
       * @param {Event} e - The event object triggered by the change action
       * @returns {Promise<any>} - The response from the server
       */
      async changeHandler(e) {
        if (!this.form) return;

        e.preventDefault();

        if (this.controller) {
          this.controller.abort();
        }

        this.dataset.loading = 'true';
        Array.from(this.form.elements).forEach((element) => {
          if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
            element.readOnly = true;
          }
        });

        this.controller = new AbortController();
        const signal = this.controller.signal;

        const config = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
          },
          body: new FormData(this.form),
          signal,
        };

        const response = await fetch(this.form.action, config).catch((error) => {
          if (error.name === 'AbortError') {
            return null;
          }

          throw error;
        });

        if (!response) {
          this.removeAttribute('data-loading');

          return null;
        }

        const data = await response.json();

        if (response.ok) {
          this.dataset.loading = 'true';

          await this.reloadSection();

          this.dataset.loading = 'false';
          Array.from(this.form.elements).forEach((element) => {
            if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
              element.readOnly = false;
            }
          });

          window.updateCartStateDisplays(data.item_count);

          return data;
        }

        this.dataset.loading = 'false';
        Array.from(this.form.elements).forEach((element) => {
          if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
            element.readOnly = false;
          }
        });

        throw new Error(data.description || 'Failed to update cart');
      }

      /** @param {Event} e - The event object triggered by the delete action */
      async deleteHandler(e) {
        if (
          e.target &&
          e.target instanceof HTMLAnchorElement &&
          e.target.closest('a') &&
          e.target.href.includes('/cart/change')
        ) {
          e.preventDefault();

          const quantity = e.target.closest('[data-cart-item]')?.querySelector('select[name="updates[]"]');

          if (quantity && quantity instanceof HTMLSelectElement) {
            quantity.value = '0';

            this.form?.dispatchEvent(new Event('change'));
          }
        }
      }

      async reloadSection() {
        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        const config = {
          method: 'GET',
          signal,
        };

        const response = await fetch(`${window.Shopify.routes.root}?sections=section-main-cart`, config).catch(
          (error) => {
            if (error.name === 'AbortError') {
              return null;
            }

            throw error;
          },
        );

        if (!response) return null;

        const data = await response.json();

        if (response.ok) {
          if (data['section-main-cart']) {
            const html = new DOMParser().parseFromString(data['section-main-cart'], 'text/html');
            const component = html.querySelector('cart-form');

            if (component) {
              this.replaceWith(component);
            }
          }

          return data;
        }

        throw new Error(response.statusText || 'Failed to reload cart section');
      }
    },
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLFormElement | null} */
        this.form = this.querySelector('form#product-form');

        this.options = this.querySelector('product-option-selector');

        /** @type {HTMLButtonElement | null} */
        this.submit = this.querySelector('[type="submit"]');
        this.controller = null;
      }

      connectedCallback() {
        if (this.form) {
          this.form.addEventListener('submit', this.submitHandler.bind(this));

          if (this.options) this.form.addEventListener('change', this.changeHandler.bind(this));
        }
      }

      /**
       * @param {Event} e - The event object triggered by the submit action
       * @returns {Promise<any>} - The response from the server
       */
      async submitHandler(e) {
        if (!this.form) return;

        e.preventDefault();

        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        if (this.submit) {
          this.submit.disabled = true;
          this.submit.dataset.loading = 'true';
        }

        const config = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
          },
          body: new FormData(this.form),
          signal,
        };

        const response = await fetch(this.form.action, config).catch((error) => {
          if (error.name === 'AbortError') {
            return null;
          }

          throw error;
        });

        if (response) {
          const data = await response.json();

          if (this.submit) {
            this.submit.disabled = false;
            this.submit.dataset.loading = 'false';
          }

          if (response.ok) {
            window.updateCartStateDisplays();

            const cartTray = window.traps.store['cart-tray'];

            if (cartTray) {
              cartTray.activate();
            }

            return data;
          }
        }

        throw new Error(response?.statusText || 'Failed to add to cart');
      }

      /**
       * @param {Event} e - The form submit event
       * @returns {Promise<any>} - The response from the server
       */
      async changeHandler(e) {
        const target = e.target;

        if (this.form && this.options && this.options instanceof HTMLElement && target instanceof HTMLElement) {
          const url =
            /** @type {HTMLInputElement | null} */
            (target.closest('[data-product-url]') ?? target.closest('[data-product-url]'))?.dataset.productUrl ||
            location.pathname;

          /** @type {HTMLInputElement | null} */
          const input = this.options.querySelector('input[type="radio"]:checked');

          const variant = input?.dataset.optionVariantId || null;

          if (!variant) {
            return null;
          }

          if (this.controller) {
            this.controller.abort();
          }

          this.dataset.loading = 'true';
          Array.from([this.form.elements, this.submit].flat()).forEach((element) => {
            if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
              element.readOnly = true;
            }

            if (element instanceof HTMLButtonElement) {
              element.disabled = true;
            }
          });

          document.dispatchEvent(
            new CustomEvent('product:variant-changed', {
              detail: { variantId: variant },
            }),
          );

          this.controller = new AbortController();
          const signal = this.controller.signal;

          const config = {
            method: 'GET',
            signal,
          };

          const response = await fetch(`${url}?sections=section-main-product&variant=${variant}`, config).catch(
            (error) => {
              if (error.name === 'AbortError') {
                return null;
              }

              throw error;
            },
          );

          if (!response) {
            this.removeAttribute('data-loading');

            return null;
          }

          const data = await response.json();

          this.dataset.loading = 'false';
          Array.from([this.form.elements, this.submit].flat()).forEach((element) => {
            if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
              element.readOnly = false;
            }

            if (element instanceof HTMLButtonElement) {
              element.disabled = false;
            }
          });

          if (response.ok) {
            if (data['section-main-product']) {
              const html = new DOMParser().parseFromString(data['section-main-product'], 'text/html');

              document.dispatchEvent(
                new CustomEvent('product:variant-changed-section-data', {
                  detail: {
                    variantId: variant,
                    section: html,
                  },
                }),
              );

              const element = html.querySelector('.shopify-section product-form');

              if (element) {
                const id = target.closest('input[id]')?.id;

                this.replaceWith(element);

                if (id) {
                  const input = document.querySelector(`#${id}`);

                  if (input && input instanceof HTMLInputElement) {
                    input.focus();
                  }
                }
              }
            }

            return data;
          }

          throw new Error(data.description || 'Failed to change product option');
        }
      }
    },
  );
}

if (!customElements.get('animated-details')) {
  customElements.define(
    'animated-details',
    class AnimatedDetails extends HTMLElement {
      static {
        window.addEventListener('click', AnimatedDetails.handleClick);
      }

      /**
       * @param {Event} e - The event object triggered by the click action
       */
      static handleClick(e) {
        if (e.target instanceof HTMLElement && e.target.closest('[data-details-id]')) {
          const detailsId = e.target.closest('[data-details-id]')?.getAttribute('data-details-id');
          if (!detailsId) return;

          const detailsElement = document.querySelector(`animated-details[data-details="${detailsId}"]`);
          if (detailsElement instanceof AnimatedDetails) {
            detailsElement.scrollIntoView({
              block: 'center',
            });
            detailsElement.expand();
          }
        }
      }

      constructor() {
        super();

        /** @type {number} */
        this.duration =
          parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--transition-short')) * 1000 || 250;

        /** @type {HTMLDetailsElement | null} */
        this.details = this.querySelector('details');

        /** @type {HTMLElement | null} */
        this.summary = this.querySelector('summary');

        /** @type {Element | null | undefined} */
        this.content = this.details?.lastElementChild;

        /** @type {Animation | null} */
        this.animation = null;

        /** @type {boolean} */
        this.closing = false;

        /** @type {boolean} */
        this.expanding = false;

        /** @type {boolean} - Closes all sibling details within parent container when opening */
        this.exclusive = this.hasAttribute('exclusive');

        /** @type {boolean} - Opens the details element when the viewport matches the desktop media query */
        this.desktopOpen = this.hasAttribute('desktop-open');

        /** @type {string | null} */
        this.detailsId = this.getAttribute('data-details');
      }

      connectedCallback() {
        const desktopMediaQuery = window.matchMedia('(min-width: 768px)');

        /** @param {boolean} matches - The state of the media query */
        const handleMediaQueryChange = (matches) => {
          if (this.details) {
            if (matches) {
              this.details.open = true;

              if (this.summary) {
                this.summary.tabIndex = -1;
              }
            } else {
              this.details.open = false;

              if (this.summary) {
                this.summary.tabIndex = 0;
              }
            }
          }
        };

        this.summary?.addEventListener('click', (e) => {
          const target = e.target;

          if (target) {
            if (target instanceof HTMLInputElement) {
              return;
            }

            e.preventDefault();

            if (!this.desktopOpen || (this.desktopOpen && !desktopMediaQuery.matches)) {
              this.toggle();
            }
          }
        });

        if (this.hasAttribute('desktop-open')) {
          handleMediaQueryChange(desktopMediaQuery.matches);

          if (desktopMediaQuery.addListener) {
            desktopMediaQuery.addListener((e) => handleMediaQueryChange(e.matches));
          } else {
            desktopMediaQuery.addEventListener('change', (e) => handleMediaQueryChange(e.matches));
          }
        }
      }

      toggle() {
        if (this.closing || !this.details?.open) {
          this.expand();
        } else if (this.expanding || this.details?.open) {
          this.shrink();
        }
      }

      expand() {
        if (!this.details) return;

        this.expanding = true;
        this.details.style.overflow = 'hidden';
        this.details.style.height = `${this.details.offsetHeight}px`;
        this.details.open = true;

        window.requestAnimationFrame(() => {
          if (this.details && this.summary && this.content && this.content instanceof HTMLElement) {
            const startHeight = `${this.details.offsetHeight}px`;
            const endHeight = `${this.summary?.offsetHeight + (this.content.offsetHeight || 0)}px`;

            if (this.animation) {
              this.animation.cancel();
            }

            this.animation = this.details.animate(
              {
                height: [startHeight, endHeight],
              },
              {
                duration: this.duration,
                easing: 'ease',
              },
            );

            this.animation.onfinish = () => this.onAnimationFinish(true);
            this.animation.oncancel = () => (this.expanding = false);

            if (this.exclusive) {
              const siblings = this.parentElement?.querySelectorAll('animated-details:has(details[open])') || [];

              siblings.forEach((sibling) => {
                if (sibling !== this && sibling instanceof AnimatedDetails) {
                  sibling.shrink();
                }
              });
            }
          }
        });
      }

      shrink() {
        if (!this.details) return;

        this.closing = true;
        this.details.style.overflow = 'hidden';

        const startHeight = `${this.details.offsetHeight}px`;
        const endHeight = `${this.summary?.offsetHeight}px`;

        if (this.animation) {
          this.animation.cancel();
        }

        this.animation = this.details.animate(
          {
            height: [startHeight, endHeight],
          },
          {
            duration: this.duration,
            easing: 'ease',
          },
        );

        this.animation.onfinish = () => this.onAnimationFinish(false);
        this.animation.oncancel = () => (this.closing = false);
      }

      /** @param {boolean} open - The state of the element */
      onAnimationFinish(open) {
        if (!this.details) return;

        this.details.open = open;
        this.animation = null;
        this.closing = false;
        this.expanding = false;
        this.details.style.height = this.details.style.overflow = '';
      }
    },
  );
}

if (!customElements.get('animated-tabs')) {
  customElements.define(
    'animated-tabs',
    class AnimatedTabs extends HTMLElement {
      static {
        window.addEventListener('click', AnimatedTabs.handleClick);
      }

      /**
       * Static handler for global clicks
       * @param {Event} e - The event object triggered by the click action
       */
      static handleClick(e) {
        if (e.target instanceof Element && e.target.closest('[data-tab-id]')) {
          /** @type {HTMLElement | null} */
          const tab = e.target.closest('[data-tab-id]');

          if (!tab) return;

          const tabId = tab.dataset.tabId;
          if (!tabId) return;

          const tabsComponent = document.querySelector(`animated-tabs:has(> [data-panels-list] > [id="${tabId}"])`);

          if (tabsComponent instanceof AnimatedTabs) {
            tabsComponent.switch(tabId);
          }
        }
      }

      /** True when the selected tab was shown at the time it was chosen */
      #selectedWhileVisible = true;

      constructor() {
        super();

        /** @type {HTMLElement | null} */
        this.list = this.querySelector('[data-tabs-list]');

        /** @type {HTMLElement[]} */
        this.panels = Array.from(this.querySelectorAll(':scope > [data-panels-list] > [data-tabs-panel]'));

        /** @type {HTMLButtonElement[]} */
        this.tabs = [];
      }

      connectedCallback() {
        if (this.list && this.panels) {
          this.tabs = Array.from(this.list.querySelectorAll('button[data-tab-id]'));

          const initialTab = this.#visibleTabs()[0] ?? this.tabs[0];
          const initialId = initialTab?.dataset.tabId;

          this.list.setAttribute('role', 'tablist');
          this.tabs.forEach((tab) => {
            const selected = tab === initialTab;

            tab.setAttribute('role', 'tab');
            tab.setAttribute('aria-selected', selected ? 'true' : 'false');
            tab.setAttribute('tabindex', selected ? '0' : '-1');
          });

          this.panels.forEach((panel, idx) => {
            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('tabindex', '-1');

            const hidden = initialId ? panel.id !== initialId : idx !== 0;

            if (hidden) panel.setAttribute('hidden', 'true');
          });

          this.addEventListener('keydown', this.handleKeydown);
          window.addEventListener('resize', this.#ensureSelectedTabIsVisible);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('resize', this.#ensureSelectedTabIsVisible);
      }

      /** @param {KeyboardEvent} e - The event object triggered by the keydown action */
      handleKeydown = (e) => {
        const target = e.target;

        if (target instanceof HTMLElement) {
          if (target instanceof HTMLButtonElement && !this.tabs.includes(target)) return;

          const component = target.closest('animated-tabs');

          if (component !== this) return;

          /** @type {HTMLButtonElement | null} */
          const tab = target.closest('button[data-tab-id]');

          if (!tab || !this.tabs.includes(tab)) return;

          switch (e.key) {
            case 'ArrowLeft':
              this.prev();
              break;
            case 'ArrowRight':
              this.next();
              break;
            case 'Home': {
              e.preventDefault();
              const firstTab = this.#visibleTabs()[0];

              this.switch(firstTab?.dataset.tabId);
              break;
            }
            case 'End': {
              e.preventDefault();
              const visibleTabs = this.#visibleTabs();
              const lastTab = visibleTabs[visibleTabs.length - 1];

              this.switch(lastTab?.dataset.tabId);
              break;
            }
          }
        }
      };

      prev() {
        this.#move(-1);
      }

      next() {
        this.#move(1);
      }

      /**
       * Moves selection among tabs that are actually shown
       * @param {number} direction - -1 for the previous tab, 1 for the next
       */
      #move(direction) {
        const tabs = this.#visibleTabs();

        if (!tabs.length) return;

        const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);
        let newIndex = direction > 0 ? 0 : tabs.length - 1;

        if (currentIndex !== -1) {
          newIndex = (currentIndex + direction + tabs.length) % tabs.length;
        }

        const tabId = tabs[newIndex]?.dataset.tabId;

        if (tabId) this.switch(tabId);
      }

      /**
       * Tabs hidden with display: none stay in the list, but they have no box to focus or scroll to
       * @param {HTMLButtonElement} tab - Tab button to test
       * @returns {boolean} Whether the tab is shown
       */
      #isTabVisible(tab) {
        const item = tab.closest('li') ?? tab;

        return getComputedStyle(item).display !== 'none';
      }

      /**
       * @returns {HTMLButtonElement[]} Tabs that are currently shown
       */
      #visibleTabs() {
        return this.tabs.filter((tab) => this.#isTabVisible(tab));
      }

      /**
       * Tabs in a closed tray are not on screen. Pages without a tray always are.
       * @returns {boolean} Whether the tabs are on screen
       */
      #isTrayOpen() {
        const modal = this.closest('[data-focus-trap]');

        if (modal) return modal.getAttribute('aria-expanded') === 'true';

        return !this.closest('modal-trap');
      }

      /**
       * Moves selection onto a shown tab after the viewport hides the tab that was chosen.
       * A panel opened while its tab was already hidden stays selected across later resizes.
       */
      #ensureSelectedTabIsVisible = () => {
        // A closed tray is display: none. switch() would focus into it, and #revealTab would loop.
        if (!this.#isTrayOpen()) return;

        const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');

        if (!selected || this.#isTabVisible(selected) || !this.#selectedWhileVisible) return;

        const fallbackId = this.#visibleTabs()[0]?.dataset.tabId;

        if (fallbackId) this.switch(fallbackId);
      };

      /**
       *  @param {string | undefined} id - The new tab to switch to.
       */
      switch(id) {
        if (!id) return;

        const tab = this.tabs.find((t) => t.dataset.tabId === id);
        const panel = this.panels.find((p) => p.id === id);

        if (!tab || !panel) return;

        this.tabs.forEach((t) => {
          t.setAttribute('aria-selected', 'false');
          t.setAttribute('tabindex', '-1');
        });

        this.panels.forEach((p) => (p.hidden = true));

        const visible = this.#isTabVisible(tab);

        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');
        panel.hidden = false;
        this.#selectedWhileVisible = visible;

        // A hidden tab, or one inside a closed tray, has no box to focus or scroll to
        if (!visible || !this.#isTrayOpen()) return;

        tab.focus();
        this.#revealTab(tab);
      }

      /**
       * Keeps the active tab fully visible within a horizontally overflowing tab list
       * @param {HTMLButtonElement} tab - The tab to bring into view
       * @param {number} [attempt] - Frames already waited for an opening tray to lay out
       */
      #revealTab(tab, attempt = 0) {
        const list = this.list;

        if (!list || !this.#isTabVisible(tab)) return;

        // Opening trays need a frame before they have a box. A closed tray stays at 0 and must not loop.
        if (!list.clientWidth) {
          const listIsShown = getComputedStyle(list).display !== 'none';
          const waitingForLayout = listIsShown && this.#isTrayOpen() && attempt < 8;

          if (!waitingForLayout) return;

          requestAnimationFrame(() => this.#revealTab(tab, attempt + 1));
          return;
        }

        const item = tab.closest('li') ?? tab;
        const itemBounds = item.getBoundingClientRect();

        // A display: none tab reports a 0×0 box, which would scroll the visible tabs out of place
        if (!itemBounds.width && !itemBounds.height) return;

        const listBounds = list.getBoundingClientRect();
        const startOverflow = itemBounds.left - listBounds.left;
        const endOverflow = itemBounds.right - listBounds.right;

        if (startOverflow >= 0 && endOverflow <= 0) return;

        list.scrollBy({ left: startOverflow < 0 ? startOverflow : endOverflow, behavior: 'smooth' });
      }
    },
  );
}

(function openExternalLinksInNewTab() {
  Array.from(document.links).forEach((link) => {
    if (link.hostname !== window.location.hostname) {
      link.target = '_blank';
      link.rel = 'noreferrer noopener';
    }
  });
})();

if (!customElements.get('notify-me-form')) {
  customElements.define(
    'notify-me-form',
    class NotifyMeForm extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLFormElement | null} */
        this.form = this.querySelector('form#notify-me-form');

        /** @type {HTMLButtonElement | null} */
        this.newsletter = this.form?.querySelector('[name="newsletter"]') ?? null;

        /** @type {HTMLElement | null} */
        this.collapsible = this.form?.querySelector('[data-collapsible-content]') ?? null;

        /** @type {HTMLButtonElement | null} */
        this.submit = this.form?.querySelector('[type="submit"]') ?? null;

        /** @type {AbortController | null} */
        this.controller = null;

        /** @type {HTMLElement | null} */
        this.errorMessageContainer = this.querySelector('[data-error-message]');
      }

      connectedCallback() {
        if (this.form) {
          this.form.addEventListener('submit', this.submitHandler.bind(this));
        }

        if (this.newsletter && this.collapsible) {
          this.newsletter.addEventListener('change', () => {
            this.toggleCollapsibleContent();
          });
        }

        document.addEventListener('click', (e) => {
          this.setProductData(e);
        });
      }

      /**
       * @param {Event} e - The event object triggered by the submit action
       * @returns {Promise<any>} - The response from the server or null
       */
      async submitHandler(e) {
        if (!this.form) return;

        e.preventDefault();

        if (this.controller) {
          this.controller.abort();
        }

        if (this.submit) {
          this.submit.disabled = true;
          this.submit.dataset.loading = 'true';
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        const formData = new FormData(this.form);
        const email = formData.get('email')?.toString() || '';
        const variantId = formData.get('variant_id')?.toString() || '';
        const isNewsletterChecked = this.newsletter instanceof HTMLInputElement && this.newsletter.checked;
        const interest = formData.get('interest')?.toString() || '';

        try {
          const payload = {
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email,
                    },
                  },
                },
                channels: ['EMAIL'],
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${variantId}`,
                  },
                },
              },
            },
          };

          const config = {
            method: 'POST',
            headers: {
              accept: 'application/vnd.api+json',
              revision: '2025-01-15',
              'content-type': 'application/vnd.api+json',
            },
            body: JSON.stringify(payload),
            signal,
          };

          const response = await fetch(
            'https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=WCbtTN',
            config,
          );

          if (response.status !== 202) {
            /** @type {Error & { status?: number }} */
            const error = new Error(`Back-in-stock subscription failed with status ${response.status}`);
            error.name = 'BackInStockSubscriptionError';
            error.status = response.status;
            throw error;
          }

          if (isNewsletterChecked) {
            const payload = {
              data: {
                type: 'subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email: email,
                        properties: {
                          Interest: interest,
                        },
                        subscriptions: {
                          email: {
                            marketing: {
                              consent: 'SUBSCRIBED',
                            },
                          },
                        },
                      },
                    },
                  },
                  custom_source: 'notify-me-form',
                },
                relationships: {
                  list: {
                    data: {
                      type: 'list',
                      id: 'YwGeYA',
                    },
                  },
                },
              },
            };

            const config = {
              method: 'POST',
              headers: {
                Accept: 'application/vnd.api+json',
                Revision: '2025-01-15',
                'Content-Type': 'application/vnd.api+json',
              },
              body: JSON.stringify(payload),
            };

            const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=WCbtTN', config);

            if (!response.ok) {
              // Newsletter subscription failed, but back in stock subscription was successful
              // We'll continue without showing an error to the user since the primary action succeeded
            }
          }

          if (this.submit) {
            this.dataset.success = 'true';
            this.submit.disabled = false;
            this.submit.dataset.loading = 'false';
          }

          return true;
        } catch (/** @type {any} */ error) {
          if (error.name === 'AbortError') {
            return null;
          }

          window.Sentry?.captureException(error, {
            tags: {
              feature: 'notify-me-form',
              error_type: error.name || 'Error',
            },
            extra: {
              variantId,
              status: error.status,
            },
          });

          this.showErrors(['Something went wrong. Please try again later.']);

          return null;
        }
      }

      toggleCollapsibleContent() {
        if (this.newsletter instanceof HTMLInputElement && this.collapsible) {
          const checked = this.newsletter.checked;
          this.collapsible.dataset.open = checked ? 'true' : 'false';

          const focusable = this.collapsible.querySelectorAll('input, [tabindex]:not([tabindex="-1"])');

          focusable.forEach((element) => {
            if (element instanceof HTMLElement) {
              element.tabIndex = checked ? 0 : -1;
            }
          });
        }
      }

      /** @param {Event} e - The event object triggered by the click action */
      setProductData(e) {
        const target = e.target;

        if (!(target instanceof HTMLButtonElement)) return;

        const button = target.closest('[data-focus-trap-toggle="notify-me-tray"][data-product-variant-id]');
        if (!button) return;

        const variantId = button.getAttribute('data-product-variant-id');
        if (!variantId) return;

        const variantInput = this.form?.querySelector('[name="variant_id"]');
        if (!(variantInput instanceof HTMLInputElement)) return;

        this.resetForm();

        variantInput.value = variantId;

        const gender = button.getAttribute('data-gender');

        if (gender === 'female') {
          const input = this.form?.querySelector('[name="interest"][value="Womens"]');
          if (input instanceof HTMLInputElement) {
            input.checked = true;
          }
        }

        if (gender === 'male') {
          const input = this.form?.querySelector('[name="interest"][value="Mens"]');
          if (input instanceof HTMLInputElement) {
            input.checked = true;
          }
        }
      }

      resetForm() {
        this.removeAttribute('data-success');

        if (this.errorMessageContainer) {
          this.errorMessageContainer.innerHTML = '';
        }
      }

      /** @param {string[]} errors - The errors to display */
      showErrors(errors) {
        const errorContainer = this.querySelector('.form-errors');

        if (!errorContainer) return;

        errorContainer.innerHTML = errors.map((/** @type {string} */ error) => `<li>${error}</li>`).join('');
      }
    },
  );
}

(function scrollFade() {
  const items = document.querySelectorAll('[data-scroll-fade]');

  items.forEach((item) => {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (item instanceof HTMLElement) {
            if (entry.isIntersecting) {
              item.dataset.scrollFade = 'true';

              observer.unobserve(item);
            } else {
              item.dataset.scrollFade = 'false';
            }
          }
        });
      },
      {
        threshold: 0.05,
      },
    );

    observer.observe(item);
  });
})();

(function containerFade() {
  const containers = document.querySelectorAll('[data-container-fade]');
  const edgeTolerance = 1;

  for (const element of containers) {
    if (!(element instanceof HTMLElement)) continue;

    const container = element;

    /** Updates the mask to match the container's current scroll position. */
    function updateFade() {
      const maxScroll = container.scrollWidth - container.clientWidth;
      const scrollPosition = Math.abs(container.scrollLeft);

      if (maxScroll <= edgeTolerance) {
        container.dataset.containerFade = 'none';
        return;
      }

      if (scrollPosition <= edgeTolerance) {
        container.dataset.containerFade = 'end';
        return;
      }

      container.dataset.containerFade = scrollPosition >= maxScroll - edgeTolerance ? 'start' : 'both';
    }

    const resizeObserver = new ResizeObserver(updateFade);

    container.addEventListener('scroll', updateFade, { passive: true });
    resizeObserver.observe(container);
    updateFade();
  }
})();
